import { DropdownMenu, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuTrigger } from '@workspace/ui/components/dropdown-menu';
import { Keyboard } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

export interface ShortcutHelpItem {
    /** Touches à afficher (ex: ['M']) */
    keys: string[];
    label: string;
    /** Précision affichée en second plan (contexte, restriction...) */
    hint?: string;
}

export interface ShortcutHelpGroup {
    title: string;
    items: ShortcutHelpItem[];
}

interface KeyboardShortcutsMenuProps {
    groups: ShortcutHelpGroup[];
    /** Ouverture pilotée de l'extérieur, pour l'ouvrir au clavier */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Classes du bouton déclencheur */
    className?: string;
}

export function Kbd({ children }: { children: ReactNode }) {
    return (
        <kbd className="inline-flex size-6 shrink-0 items-center justify-center rounded border border-border bg-muted font-mono text-xs font-semibold text-foreground">
            {children}
        </kbd>
    );
}

/**
 * Lexique des raccourcis clavier, voisin de la bascule clair / sombre.
 *
 * Les raccourcis d'une lettre ne s'annoncent nulle part ailleurs : sans cette liste, ils ne
 * servent qu'à ceux qui les ont appris, et surprennent les autres, qui les déclenchent sans
 * savoir d'où vient l'onglet qui s'ouvre.
 *
 * Rien n'est cliquable ici : c'est une notice. Les entrées sont donc des lignes ordinaires et non
 * des éléments de menu, qui prendraient le focus et laisseraient croire à une action.
 */
export function KeyboardShortcutsMenu({ groups, open, onOpenChange, className }: KeyboardShortcutsMenuProps) {
    return (
        <DropdownMenu
            open={open}
            onOpenChange={onOpenChange}
        >
            <DropdownMenuTrigger
                aria-label="Raccourcis clavier"
                title="Raccourcis clavier (?)"
                className={`flex size-9 cursor-pointer items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground ${className ?? ''}`}
            >
                <Keyboard
                    className="size-4"
                    aria-hidden="true"
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="min-w-72"
            >
                {groups.map((group, index) => (
                    <Fragment key={group.title}>
                        {index > 0 && <DropdownMenuSeparator className="my-2" />}
                        <p className={`px-3 pb-2 text-xs text-muted-foreground ${index > 0 ? 'pt-1' : 'pt-2'}`}>{group.title}</p>
                        {group.items.map((item) => (
                            <div
                                key={item.label}
                                className="flex items-center gap-3 px-3 py-1.5 text-sm"
                            >
                                {item.keys.map((key) => (
                                    <Kbd key={key}>{key}</Kbd>
                                ))}
                                <span>
                                    {item.label}
                                    {item.hint && <span className="text-muted-foreground"> ({item.hint})</span>}
                                </span>
                            </div>
                        ))}
                    </Fragment>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
